// src/pages/user/Settings.js
// Page paramètres avec vraies permissions device (géoloc + notifications) - NE PAS MODIFIER

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/shared/LanguageSwitcher';
import LocationPermissionModal from '../../components/shared/LocationPermissionModal';
import { useTheme } from '../../hooks/useTheme';

const Settings = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();

  // 'granted' | 'denied' | 'prompt' | 'unsupported' | 'checking'
  const [geoStatus, setGeoStatus] = useState('checking');
  const [notifStatus, setNotifStatus] = useState('checking');
  const [showGeoGuide, setShowGeoGuide] = useState(false);

  // Vérifie l'état réel des permissions au chargement (et reste synchronisé si changé ailleurs)
  useEffect(() => {
    let geoPermission;

    const checkGeo = async () => {
      if (!navigator.geolocation) {
        setGeoStatus('unsupported');
        return;
      }
      if (navigator.permissions) {
        try {
          geoPermission = await navigator.permissions.query({ name: 'geolocation' });
          setGeoStatus(geoPermission.state);
          geoPermission.onchange = () => setGeoStatus(geoPermission.state);
        } catch (err) {
          setGeoStatus('prompt');
        }
      } else {
        setGeoStatus('prompt');
      }
    };

    const checkNotif = () => {
      if (!('Notification' in window)) {
        setNotifStatus('unsupported');
        return;
      }
      setNotifStatus(Notification.permission); // 'granted' | 'denied' | 'default'
    };

    checkGeo();
    checkNotif();

    return () => {
      if (geoPermission) geoPermission.onchange = null;
    };
  }, []);

  // Ne déclenche la demande native QUE si l'utilisateur n'a encore rien décidé
  const handleGeoToggle = () => {
    if (geoStatus === 'granted') return; // déjà actif, rien à faire
    if (geoStatus === 'denied') {
      setShowGeoGuide(true); // guide pour réactiver manuellement (bloqué par le navigateur)
      return;
    }
    // 'prompt' → déclenche la vraie popup native du téléphone
    navigator.geolocation.getCurrentPosition(
      () => setGeoStatus('granted'),
      () => setGeoStatus('denied'),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleNotifToggle = async () => {
    if (notifStatus === 'granted' || notifStatus === 'unsupported') return;
    if (notifStatus === 'denied') {
      alert("Les notifications sont bloquées au niveau de votre navigateur. Vous pouvez les réactiver dans les réglages du site.");
      return;
    }
    const result = await Notification.requestPermission();
    setNotifStatus(result);
  };

  const statusLabel = (status) => {
    const labels = {
      granted: 'Activé',
      denied: 'Bloqué',
      prompt: 'Non activé',
      default: 'Non activé',
      unsupported: 'Non disponible',
      checking: '...',
    };
    return labels[status] || status;
  };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.bg },
    header: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', position: 'sticky', top: 0, backgroundColor: theme.bg, zIndex: 50, borderBottom: `1px solid ${theme.borderSoft}` },
    backButton: { background: 'none', border: 'none', color: theme.textPrimary, fontSize: '20px', cursor: 'pointer', padding: '4px' },
    title: { fontSize: '20px', fontWeight: '700', color: theme.textPrimary },
    section: { padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: '8px' },
    sectionTitle: { fontSize: '13px', fontWeight: '600', color: theme.textMuted, textTransform: 'uppercase', padding: '12px 0 4px' },
    row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px', backgroundColor: theme.bgCard, borderRadius: '10px', border: `1px solid ${theme.borderSoft}` },
    rowLabel: { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '15px', color: theme.textPrimary },
    rowMeta: { display: 'flex', alignItems: 'center', gap: '8px' },
    statusText: (status) => ({
      fontSize: '12px',
      fontWeight: '600',
      color: status === 'granted' ? '#48BB78' : status === 'denied' ? '#F56565' : theme.textMuted,
    }),
    toggle: (active) => ({
      width: '48px',
      height: '28px',
      borderRadius: '14px',
      cursor: active === 'unsupported' ? 'default' : 'pointer',
      position: 'relative',
      transition: 'background-color 200ms',
      backgroundColor: active === 'granted' ? theme.gold : theme.textMuted,
      opacity: active === 'unsupported' ? 0.4 : 1,
    }),
    toggleKnob: (isOn) => ({
      position: 'absolute',
      top: '2px',
      left: isOn ? '22px' : '2px',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: '#FFFFFF',
      transition: 'left 200ms',
    }),
    hintBox: { padding: '10px 14px', backgroundColor: theme.bgInput, borderRadius: '8px', fontSize: '12px', color: theme.textSecondary, lineHeight: '1.4' },
  };

  const Toggle = ({ status, onClick }) => (
    <div style={styles.toggle(status)} onClick={status === 'unsupported' ? undefined : onClick}>
      <div style={styles.toggleKnob(status === 'granted')} />
    </div>
  );

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Paramètres | Fynho</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <div style={styles.title}>⚙️ {t('common.settings')}</div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Général</div>
        <div style={styles.row}>
          <span style={styles.rowLabel}>🌍 {t('common.language')}</span>
          <LanguageSwitcher compact />
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Permissions de l'appareil</div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>📍 Géolocalisation</span>
          <div style={styles.rowMeta}>
            <span style={styles.statusText(geoStatus)}>{statusLabel(geoStatus)}</span>
            <Toggle status={geoStatus} onClick={handleGeoToggle} />
          </div>
        </div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>🔔 Notifications</span>
          <div style={styles.rowMeta}>
            <span style={styles.statusText(notifStatus)}>{statusLabel(notifStatus)}</span>
            <Toggle status={notifStatus} onClick={handleNotifToggle} />
          </div>
        </div>

        <div style={styles.hintBox}>
          Ces réglages contrôlent les autorisations de votre appareil. Fynho les utilise pour vous montrer
          les annonces proches de vous (géolocalisation) et vous alerter (notifications, bientôt disponible).
        </div>
      </div>

      <LocationPermissionModal open={showGeoGuide} onClose={() => setShowGeoGuide(false)} />
    </div>
  );
};

export default Settings;